import CategoriesTab from "@/components/settings/CategoriesTab";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Search } from "lucide-react";
import { PageHeader } from "@/components/common/States";
import AuditLogTab from "@/components/settings/AuditLogTab";
import RatesTab from "@/components/settings/RatesTab";
import SalesSettingsTab from "@/components/settings/SalesSettingsTab";
import PrimSettings from "@/components/prim/PrimSettings";
import BackupPanel from "@/components/settings/BackupPanel";
import CompanyTab from "@/components/settings/CompanyTab";
import AutomationTab from "@/components/settings/AutomationTab";
import BalanceCheckSettings from "@/components/finance/BalanceCheckSettings";
import VaultDueSettings from "@/components/finance/VaultDueSettings";
import GS1Tab from "@/components/settings/GS1Tab";
import DataImportTab from "@/components/import/DataImportTab";
import ConstructionCategoriesTab from "@/components/settings/ConstructionCategoriesTab";
import TakipSettingsTab from "@/components/settings/TakipSettingsTab";
import ShipsGoTab from "@/components/settings/ShipsGoTab";
import TestModeTab from "@/components/settings/TestModeTab";
import ImportSettingsTab from "@/components/settings/ImportSettingsTab";
import SmartOrderSettingsTab from "@/components/settings/SmartOrderSettingsTab";
import ZekatSettingsTab from "@/components/settings/ZekatSettingsTab";
import TemplatesTab from "@/components/settings/TemplatesTab";
import AccountsAdmin from "@/components/finance/AccountsAdmin";
import SystemResetTab from "@/components/settings/SystemResetTab";

const SettingsAccounts = () => <AccountsAdmin allowDelete />;
import { useAuth } from "@/context/AuthContext";

// Dikey bölüm listesi; derin bağlantı ?tab=<anahtar> (eski bağlantılar aynen çalışır). Mobil genişlikte önce liste, sonra bölüm + geri.
export default function Settings() {
  const { can, flag, user } = useAuth();
  const admin = !!user && (user.is_super || ["super_admin", "yonetici"].includes(user.role_key));
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const sections = useMemo(() => [
    ["Genel", "company", "Firma Bilgileri", CompanyTab, true], ["Genel", "rates", "Döviz Kurları & Sistem", RatesTab, true], ["Genel", "audit", "Denetim Kaydı", AuditLogTab, true],
    ["Satış & Belgeler", "sales", "Satış Ayarları", SalesSettingsTab, true], ["Satış & Belgeler", "sablonlar", "Belge Şablonları", TemplatesTab, can("ayarlar", "sablon")],
    ["Satış & Belgeler", "personel", "Personel · Prim Ayarları", PrimSettings, can("prim", "approve") && flag("bonus_view")],
    ["Operasyon", "imports", "İthalat", ImportSettingsTab, can("ithalat")], ["Operasyon", "akilli_siparis", "Satın Alma / İthalat · Akıllı Sipariş", SmartOrderSettingsTab, can("ithalat") || can("ithalat", "oneri")], ["Operasyon", "kategoriler", "Ürün Kategorileri", CategoriesTab, can("urunler")], ["Operasyon", "insaat_kalemleri", "İnşaat Gider Kalemleri", ConstructionCategoriesTab, true],
    ["Operasyon", "takip", "Takip", TakipSettingsTab, true],
    ["Finans", "hesaplar", "Kasa / Banka / POS Hesapları", SettingsAccounts, can("finans")],
    ["Finans", "bakiye_kontrol", "Aylık Bakiye Kontrolü", BalanceCheckSettings, !!user?.is_super],
    ["Finans", "kasa_vade", "Banka Kasası Vade Uyarısı", VaultDueSettings, !!user?.is_super || can("finans", "edit")],
    ["Sistem", "gs1", "Barkod / GS1", GS1Tab, !!user?.is_super],
    ["Sistem", "backup", "Sistem · Yedekleme", BackupPanel, true], ["Sistem", "automation", "Otomasyonlar", AutomationTab, true],
    ["Sistem", "shipsgo", "Entegrasyonlar · ShipsGo", ShipsGoTab, admin], ["Sistem", "testmode", "Test Hesapları", TestModeTab, admin],
    ["Sistem", "zekat", "Zekat", ZekatSettingsTab, !!user?.is_super], ["Sistem", "import", "Veri Aktarımı", DataImportTab, can("ayarlar", "edit")],
    ["Sistem", "sifirla", "Sistemi Sıfırla", SystemResetTab, !!user?.is_super],
  ].filter((s) => s[4]), [can, flag, user, admin]);
  const tab = params.get("tab");
  const cur = sections.find((s) => s[1] === tab) || sections.find((s) => s[1] === "audit");
  const shown = sections.filter((s) => !q || s[2].toLocaleLowerCase("tr").includes(q.toLocaleLowerCase("tr")));
  const groups = [...new Set(shown.map((s) => s[0]))];
  const open = (k) => setParams({ tab: k });
  const Section = cur[3];
  return (
    <div data-testid="settings-page">
      <PageHeader title="Ayarlar" subtitle="Firma, belgeler, sistem ve entegrasyon ayarları" testid="settings-title" />
      <div className="md:grid md:grid-cols-[240px_1fr] gap-5 items-start">
        <nav className={`${tab ? "hidden md:block" : ""} panel p-2 md:sticky md:top-4`} data-testid="settings-nav">
          <div className="relative mb-2"><Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ayar ara…" className="w-full h-8 pl-7 pr-2 rounded border border-slate-200 text-[13px]" data-testid="settings-search" /></div>
          {groups.map((g) => (
            <div key={g} className="mb-2">
              <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-slate-400">{g}</div>
              {shown.filter((s) => s[0] === g).map(([, k, l]) => (
                <button key={k} onClick={() => open(k)} className={`w-full text-left px-2 py-1.5 rounded text-[13px] flex items-center justify-between transition-colors ${cur[1] === k ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"}`} data-testid={`tab-${k === "sales" ? "sales-settings" : k === "imports" ? "import-settings" : k === "import" ? "data-import" : k === "personel" ? "personel-prim" : k === "insaat_kalemleri" ? "construction-categories" : k === "takip" ? "takip-settings" : k === "zekat" ? "zekat-settings" : k}`}>
                  {l}<ChevronRight className="h-3.5 w-3.5 md:hidden opacity-50" /></button>))}
            </div>))}
        </nav>
        <section className={`${tab ? "" : "hidden md:block"} min-w-0`} data-testid={`settings-section-${cur[1]}`}>
          <button onClick={() => setParams({})} className="md:hidden mb-3 text-[13px] text-slate-600 flex items-center gap-1" data-testid="settings-back"><ArrowLeft className="h-4 w-4" />Ayarlar</button>
          <h2 className="text-base md:text-lg font-semibold mb-3" data-testid="settings-section-title">{cur[2]}</h2>
          <Section />
        </section>
      </div>
    </div>
  );
}
